import { useState, useContext, useEffect, useMemo } from 'react'
import _ from "lodash"

import { Typography, FormControlLabel, RadioGroup, Radio, Tooltip, Button, Box } from '@mui/material'
import EastRoundedIcon from '@mui/icons-material/EastRounded'
import DatasetOutlinedIcon from '@mui/icons-material/DatasetOutlined'

import { PassContext } from '../pass-context'
import dataSelector from './data-selector'
import { AppContext } from '/providers/app-context.js'
import PopupUtils from '/utils/popup-editor-utils'
import ImageSelector from '/components/image-selector'
import PopupShell from '../popup-shell'

const canDelete = {
  "meta.images.icon": false,
  "meta.images.logo": false,
  "meta.images.thumbnail": true,
  "meta.images.strip": true,
  "meta.images.stampcard": false,
  "meta.images.background": true,
}

const instanceDataPaths = {
  "meta.images.icon": "__images__.__apple_icon__",
  "meta.images.logo": "__images__.__apple_logo__",
  "meta.images.thumbnail": "__images__.__apple_thumbnail__",
  "meta.images.strip": "__images__.__apple_strip__",
  "meta.images.stampcard": "images.apple_strip",
  "meta.images.background": "__images__.__apple_background__",
}

const PopupDialogImage = ({ open, close, title, path, stampcardUrl, saveFunction }) => {

  const { template, instanceData, setTemplate, setInstanceData, passType, openInstanceDataManager } = useContext(PassContext)
  const { imageList } = useContext(AppContext)

  const [newTemplate, setNewTemplate] = useState({})
  const [newInstanceData, setNewInstanceData] = useState({})
  const [dataSource, setDataSource] = useState("template")
  const [requiredSize, setRequiredSize] = useState("")
  const [stampCardUrl, setStampCardUrl] = useState("")

  const permitInstanceData = stampcardUrl ? false :  true

  const imageType = useMemo(() => path.split(".").pop(), [path])

  const currentImageUrl = useMemo(() => {
    if (!open) return null

    if (stampcardUrl) return stampcardUrl

    let initialImageUrl = dataSelector({ template: newTemplate, instanceData: newInstanceData, path })
    const img = imageList.filter(i => i.s3_url === initialImageUrl)[0]
    return img ? img.s3_url : initialImageUrl
  }, [open,newTemplate, newInstanceData, path, stampcardUrl, imageList])

  // Runs on "open" state change or if template / instance data change 
  // Ensures we have the current data rather than something left behind from previous form 
  useEffect(() => {
    try {
      setNewTemplate(_.cloneDeep(template))
      setNewInstanceData(_.cloneDeep(instanceData))

      let tv = _.get(template, path)
      let source = tv?.toString().match(/^{{.*}}$/) ? "instance" : "template"

      setDataSource(source)
    }
    catch (err) {
      console.error(err)
    }
  }, [open, template, instanceData])

  //////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Support Functions
  //////////////////////////////////////////////////////////////////////////////////////////////////////////

  const updateNewTemplate = value => {
    const datapath = path
    PopupUtils.updateTemplate({ newTemplate, setNewTemplate, datapath, value })
  }

  const updateInstanceData = value => {
    const datapath = path
    PopupUtils.updateInstanceData({ newTemplate, newInstanceData, setNewInstanceData, datapath, value })
  }

  const updateBoth = (instanceValue, templateValue) => {
    let i = _.cloneDeep(newInstanceData)
    let t = _.cloneDeep(newTemplate)

    if (instanceValue) _.set(i, instanceDataPaths[path], instanceValue)
    else _.unset(i, instanceDataPaths[path])
    
    _.set(t, path, templateValue)

    setNewInstanceData(i)
    setNewTemplate(t)
  }

  const handleChangeDataSource = value => { // "instance" or "template"
    setDataSource(value)

    // Now swap data around to support the new datasource choice

    if (value === "instance") { // template -> instance
      // copy template value to instance data. Insert instance path into template
      const imageUrl = _.get(newTemplate, path)
      updateBoth(imageUrl, `{{${instanceDataPaths[path]}}}`)
    } else { // instance -> template
      // copy instance data into template. Set instance data to null
      const imageUrl = _.get(newInstanceData, instanceDataPaths[path])
      updateBoth(null, imageUrl)
    }
  }

  const removeImageFromTemplate = () => {
    PopupUtils.deleteDataItem({ source: newTemplate, updater: setNewTemplate, path })
  }

  const handleClose = (e, reason) => {
    if (reason && reason === "backdropClick")
      return
    close()
  }

  const saveFormData = () => {
    // Save content of our "new" template/instanceData back to state

    if (saveFunction) { // Used for stampcards
      saveFunction(stampCardUrl)
      close()
    } else {

      if (!_.isEqual(newTemplate, template)) {

        let nt = { ...newTemplate }

        if (path === "meta.images.logo") {
          const selectedImageUrl = dataSelector({ template: newTemplate, instanceData: newInstanceData, path })

          let selectedImage = imageList.filter(i => i.s3_url === selectedImageUrl)[0]
          if (selectedImage?.width > 300) { // Hide title if wide logo. Avoids seeing "..."
            nt.meta.title = " "
          }
        }

        setTemplate(nt)
      }

      if (!_.isEqual(newInstanceData, instanceData)) setInstanceData(newInstanceData)
      close()
    }
  }

  const setImageUrl = imgUrl => {
    if (saveFunction) {
      setStampCardUrl(imgUrl)
    } else {

      if (dataSource === "template")
        updateNewTemplate(imgUrl)
      else
        updateInstanceData(imgUrl)
    }
  }


  //////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Main Render
  //////////////////////////////////////////////////////////////////////////////////////////////////////////

  try {
    return (
      <PopupShell open={open} onClose={handleClose} width={1000} variant="solid">
        {/* Header */}
        <div className="flex-shrink-0 p-2">
          <div className="flex justify-between items-center">
            <Typography color="secondary" variant="h6">{title}<br/>({requiredSize})</Typography>

            {permitInstanceData && (
              <RadioGroup row value={dataSource} onChange={e => handleChangeDataSource(e.target.value)}>
                <FormControlLabel value="template" control={<Radio />} label="Same image for all passes" />
                <FormControlLabel value="instance" control={<Radio />} label="Use unique images per pass" />
              </RadioGroup>
            )}

            <Tooltip title="Press ESC to dismiss">
              <Button endIcon={<EastRoundedIcon />} onClick={handleClose}>Cancel</Button>
            </Tooltip>
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 overflow-hidden flex flex-col md:flex-row gap-5 p-2">
          <ImageSelector
            passType={passType}
            currentImageUrl={currentImageUrl}
            setImageUrl={setImageUrl}
            imageType={imageType}
            deleteAllowed={canDelete[path]}
            removeImageFromTemplate={removeImageFromTemplate}
            setRequiredSize={setRequiredSize}
          />
        </div>

        {/* Footer */}
        <Box className="flex-shrink-0 p-3">
          <hr className="my-2" />
          <div className="flex flex-col md:flex-row justify-between items-center gap-2">
            <Button startIcon={<DatasetOutlinedIcon />} onClick={openInstanceDataManager} >Edit sample data</Button>
            
            {path === "meta.images.background" && (
              <div className="text-xs p-3">
                NOTE: Foreground text is always <b>WHITE</b> on background images.
                <b>This cannot be changed</b>. Use a dark image so text is visible.
              </div>
            )}
            <div />
            <Button variant="contained" onClick={saveFormData}>Save</Button>
          </div>
        </Box>
      </PopupShell>
    )
  }
  catch (err) {
    console.error(err)
  }
}

export default PopupDialogImage
